import React, { createContext, DependencyList, useCallback, useContext, useEffect, useState } from 'react';
import {
    isLoadingOrNull,
    JC_LOADING,
    JcAsyncResult,
    JcDataError,
    JcLoading,
    JcLoadingPlaceholder,
    JcUnsubscribe,
} from 'jcsdk';
import { JcAuthService } from './JcAuthService';

/** The signed-in auth, `null` when signed out, or `JC_LOADING` while determining. */
export type JcAuthState<AuthT extends object> = AuthT | null | JcLoading;

export interface JcRequireAuthProps<AuthT extends object> {
    /** Shown in place of `children` when signed out, e.g. a sign-in wall. */
    readonly signedOut: React.ReactNode;
    /** Further gate on the signed-in auth (e.g. an admin claim). Defaults to allowing everyone. */
    readonly isAllowed?: (auth: AuthT) => boolean;
    /** Shown when `isAllowed` fails. Defaults to a loading placeholder, for a guard that redirects. */
    readonly denied?: React.ReactNode;
    readonly children: React.ReactElement;
}

export interface JcAuthBindings<AuthT extends object> {
    /** Subscribes once at the app root and provides the auth state to its subtree. */
    readonly AuthProvider: (props: { readonly children: React.ReactNode }) => React.ReactElement;
    readonly useAuthState: () => JcAuthState<AuthT>;
    /** Route guard: loading → `signedOut` → `denied` → `children`. Apps supply only the branded UI. */
    readonly RequireAuth: (props: JcRequireAuthProps<AuthT>) => React.ReactElement;
    /** The signed-in auth; throws unless rendered under a sign-in guard. */
    readonly useRequiredAuth: () => AuthT;
    /** Reloads the user and re-emits provider state (e.g. after out-of-band email verification). */
    readonly useReloadAuth: () => () => Promise<AuthT | null>;
    /**
     * Subscribes to per-user data while signed in: `value` is `JC_LOADING` while
     * pending, `null` when signed out, `T` once loaded. `deps` are the values
     * `subscribe` closes over besides the auth, as for `useEffect`.
     */
    readonly useAuthedSnapshot: <T>(
        subscribe: (auth: AuthT, onSnapshot: (value: T) => void, onError: (error: JcDataError) => void) => JcUnsubscribe,
        deps: DependencyList,
    ) => JcAsyncResult<T | null>;
}

/** Binds auth context, provider, and hooks to an app's `JcAuthService`. */
export const makeJcAuth = <AuthT extends object>(service: JcAuthService<AuthT>): JcAuthBindings<AuthT> => {
    const StateContext = createContext<JcAuthState<AuthT> | undefined>(undefined);
    const ReloadContext = createContext<(() => Promise<AuthT | null>) | undefined>(undefined);

    const AuthProvider = ({ children }: { readonly children: React.ReactNode }): React.ReactElement => {
        const [auth, setAuth] = useState<JcAuthState<AuthT>>(JC_LOADING);

        useEffect(() => service.onAuthChange(setAuth), []);

        const reloadAuth = useCallback(async (): Promise<AuthT | null> => {
            const refreshed = await service.reloadCurrentAuth();
            setAuth(refreshed);
            return refreshed;
        }, []);

        return (
            <StateContext.Provider value={auth}>
                <ReloadContext.Provider value={reloadAuth}>
                    {children}
                </ReloadContext.Provider>
            </StateContext.Provider>
        );
    };

    const useAuthState = (): JcAuthState<AuthT> => {
        const state = useContext(StateContext);
        if (state === undefined) {
            throw new Error('useAuthState must be used within its AuthProvider');
        }
        return state;
    };

    const RequireAuth = (props: JcRequireAuthProps<AuthT>): React.ReactElement => {
        const auth = useAuthState();
        if (auth === JC_LOADING) {
            return <JcLoadingPlaceholder id="jc-require-auth" />;
        }
        if (auth === null) {
            return <>{props.signedOut}</>;
        }
        if (props.isAllowed !== undefined && !props.isAllowed(auth)) {
            return <>{props.denied ?? <JcLoadingPlaceholder id="jc-require-auth-denied" />}</>;
        }
        return props.children;
    };

    const useRequiredAuth = (): AuthT => {
        const state = useAuthState();
        if (isLoadingOrNull(state)) {
            throw new Error('useRequiredAuth called without a signed-in user; wrap it in a sign-in guard');
        }
        return state;
    };

    const useReloadAuth = (): (() => Promise<AuthT | null>) => {
        const reload = useContext(ReloadContext);
        if (reload === undefined) {
            throw new Error('useReloadAuth must be used within its AuthProvider');
        }
        return reload;
    };

    const useAuthedSnapshot = <T,>(
        subscribe: (auth: AuthT, onSnapshot: (value: T) => void, onError: (error: JcDataError) => void) => JcUnsubscribe,
        deps: DependencyList,
    ): JcAsyncResult<T | null> => {
        const auth = useAuthState();
        const [value, setValue] = useState<T | null | JcLoading>(auth === null ? null : JC_LOADING);
        const [error, setError] = useState<JcDataError | null>(null);
        const [nonce, setNonce] = useState(0);

        useEffect(() => {
            setError(null);
            if (isLoadingOrNull(auth)) {
                setValue(auth);
                return;
            }
            setValue(JC_LOADING);
            return subscribe(auth, setValue, setError);
            // `subscribe` is tracked via `deps`; `nonce` forces a re-subscribe on retry.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [auth, nonce, ...deps]);

        return { value, error, retry: () => setNonce((n) => n + 1) };
    };

    return { AuthProvider, useAuthState, RequireAuth, useRequiredAuth, useReloadAuth, useAuthedSnapshot };
};
